import { PaginatedResource } from '../../typings/Paginated';
import { AlreadyExists, BaseService, ClientError, GenericPaginatedParameters, PartialBy, InvalidValue, NotFound, Unauthorized, UnexpectedError, ExtendableError } from './base-service';

export enum VacancyCancellationReasonsType {
    APPLICATION_APP_AGENT = 'application_app_agent',
    APPLICATION_WEB_PANEL = 'application_web_panel',
    VACANCY_WEB_PANEL = 'vacancy_web_panel',
}

export type VacancyPresenceStatus = 'pending' | 'present' | 'absent';
export type VacancyAbsence = 'justified' | 'unjustified';

export class CannotDeleteVacancy extends ExtendableError { }
export class HasOpenVacancies extends ExtendableError { }
export class EquipmentAlreadyInUse extends ExtendableError { }

interface PresencePaginatedResource<TData> {
    count: number;
    rows: TData[];
    summary: {
        pending: number;
        presences: number;
        absences: number;
    };
}

export class DeacService extends BaseService {
    async getVacancyLevels(params: GenericPaginatedParameters): Promise<PaginatedResource<VacancyLevel>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy-levels${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createVacancyLevel(vacancyLevel: Omit<VacancyLevelParams, 'id'>): Promise<VacancyLevel> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy-level`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(vacancyLevel)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'AlreadyExists') {
                throw new AlreadyExists();
            } else if (resJson.code == 'HasOpenVacancies') {
                throw new HasOpenVacancies();
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateVacancyLevel(vacancyLevel: PartialBy<Omit<VacancyLevelParams, 'actingBodyId'>, 'id'>) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy-level/${vacancyLevel.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(vacancyLevel)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'AlreadyExists') {
                throw new AlreadyExists();
            } else if (resJson.code == 'HasOpenVacancies') {
                throw new HasOpenVacancies();
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getVacancyLevelById(id: number): Promise<VacancyLevel> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy-level/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'HasOpenVacancies') {
                throw new HasOpenVacancies();
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteVacancyLevel(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy-level/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'HasOpenVacancies') {
                throw new HasOpenVacancies();
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async getWorkplaces(params: WorkplacesPaginatedParameters): Promise<PaginatedResource<Workplace>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/workplaces${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createWorkplace(workplace: Omit<WorkplaceParams, 'id' | 'usersIdsToUnblock'>) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/workplace`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(workplace)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'AlreadyExists') {
                throw new AlreadyExists();
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateWorkplaces(workplace: PartialBy<Omit<WorkplaceParams, 'actingBodyId' | 'actingBodyUnitId' | 'actingBodyCommandId'>, 'id'>) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/workplace/${workplace.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(workplace)
        });

        if (res.status == 400) {
            throw new InvalidValue();
        } else if (res.status == 403) {
            throw new Unauthorized();
        } else if (res.status == 404) {
            throw new NotFound();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getWorkplaceById(id: number): Promise<Workplace> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/workplace/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteWorkplace(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/workplace/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 403) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'HasOpenVacancies') {
                throw new HasOpenVacancies();
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 204) {
            throw new UnexpectedError();
        }

    }

    async getVacancyTags(params: Omit<GenericPaginatedParameters, 'actingBodyId'>): Promise<PaginatedResource<VacancyTag>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy-tags${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createVacancyTag(vacancyTag: Omit<CrateUpdateVacancyTag, 'id'>): Promise<VacancyTag> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy-tag`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(vacancyTag)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 400) {
            throw new InvalidValue();
        }

        if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateVacancyTag(vacancyTag: CrateUpdateVacancyTag): Promise<VacancyTag> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy-tag/${vacancyTag.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(vacancyTag)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 400) {
            throw new InvalidValue();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getVacancyTagById(id: number): Promise<VacancyTag> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy-tag/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteVacancyTag(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy-tag/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async getVacancyCancellationReasons(params: Omit<GenericPaginatedParameters, 'actingBodyId'>): Promise<PaginatedResource<VacancyCancellationReason>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy-cancellation-reasons${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getVacancyCancellationReasonById(id: number): Promise<VacancyCancellationReason> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy-cancellation-reason/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createVacancyCancellationReason(cancellationReason: Omit<VacancyCancellationReason, 'id'>): Promise<VacancyCancellationReason> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy-cancellation-reason`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(cancellationReason)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'AlreadyExists') {
                throw new AlreadyExists();
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateVacancyCancellationReason(cancellationReason: PartialBy<VacancyCancellationReason, 'id'>): Promise<VacancyCancellationReason> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy-cancellation-reason/${cancellationReason.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(cancellationReason)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'AlreadyExists') {
                throw new AlreadyExists();
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteVacancyCancellationReason(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy-cancellation-reason/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async getVacancies(params: VacanciesPaginatedParameters): Promise<PaginatedResource<Vacancy>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancies${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async closeVacanciesApplications(params: { vacanciesIds: number[]; applicationsFinish: Date; }) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancies/close-applications`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }


    async createVacancy(params: Omit<CreateVacancyParams, 'id'>): Promise<Vacancy> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateVacancy(params: PartialBy<CreateVacancyParams, 'id'>): Promise<Vacancy> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy/${params.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getVacancy(id: number): Promise<Vacancy> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteVacancy(id: number, cancellationReasonId: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                cancellationReasonId
            })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'CannotDeleteVacancy') {
                throw new CannotDeleteVacancy();
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async deleteVacancies(params: { vacanciesIds: number[]; cancellationReasonId: number; }) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancies`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'CannotDeleteVacancy') {
                throw new CannotDeleteVacancy();
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async getVacancyApplications(id: number, params: Omit<GenericPaginatedParameters, 'actingBodyId'>): Promise<PaginatedResource<VacancyApplication>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy/${id}/applications${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getRolesSimplified(actingBodyId: number): Promise<RoleSimplified[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/roles-simplified${this.encodeQueryParams({ actingBodyId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getVacancyLevelsSimplified(actingBodyId: number): Promise<SimplifiedVacancyLevel[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy-levels-simplified${this.encodeQueryParams({ actingBodyId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getWorkplacesSimplified(params: { actingBodyId: number; actingBodyUnitId?: number; actingBodyCommandId?: number; }): Promise<SimplifiedWorkplace[]> {
        const token = await this.getToken();

        const isAgentApp = process.env.AGENT_FRONTEND_DOMAIN ? window.location.host.startsWith(process.env.AGENT_FRONTEND_DOMAIN) : false;

        const res = await fetch(`${this.centralEndpoint}${isAgentApp ? '/app-agent' : ''}/deac/workplaces-simplified${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getTagsSimplified(): Promise<VacancyTag[]> {
        const token = await this.getToken();

        const isAgentApp = process.env.AGENT_FRONTEND_DOMAIN ? window.location.host.startsWith(process.env.AGENT_FRONTEND_DOMAIN) : false;

        const res = await fetch(`${this.centralEndpoint}${isAgentApp ? '/app-agent' : ''}/deac/vacancy-tags-simplified`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getRequirementsSimplified(actingBodyId: number): Promise<BaseSkill[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/requirements-simplified${this.encodeQueryParams({ actingBodyId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getCancellationReasonsByPurpose(purpose: VacancyCancellationReasonsType): Promise<CancellationReason[]> {
        const token = await this.getToken();

        const isAgentApp = process.env.AGENT_FRONTEND_DOMAIN ? window.location.host.startsWith(process.env.AGENT_FRONTEND_DOMAIN) : false;

        const res = await fetch(`${this.centralEndpoint}${isAgentApp ? '/app-agent' : ''}/deac/vacancy-cancellation-reasons-by-purpose${this.encodeQueryParams({ purpose })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getUsers(params: { actingBodyId?: number; actingBodyUnitId?: number; }): Promise<SimplifiedUser[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/users-simplified${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async cancelVacancyApplications(vacancyId: number, params: { applicationsIds?: number[]; cancellationReasonId: number; }) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy/${vacancyId}/applications`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'CannotDeleteVacancy') {
                throw new CannotDeleteVacancy();
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getSchedule(params: VacanciesScheduleParameters): Promise<ScheduleInfo> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancies/schedule${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPresences(params: PresencesPaginatedParameters): Promise<PresencePaginatedResource<Presence>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/presences${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async presenceRegistration(params: {
        applications: Record<number, number | null>;
        present: boolean;
        absentJustification?: VacancyAbsence;
    }) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/presence-registration`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPresencesReport(params: PresencesPaginatedParameters) {
        const token = await this.getToken();

        const headers = this.getHeaders(token, 'application/xlsx;charset=utf-8');
        headers.set('Accept', 'application/xlsx');
        const res = await fetch(`${this.centralEndpoint}/deac/presences-report${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: headers,
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.blob();
    }

    async getReports(params: GenericPaginatedParameters): Promise<PaginatedResource<DeacVacanciesReport>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/reports/paginated${this.encodeQueryParams({
            ...params,
        })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getReport(id: number): Promise<{ resultUrl: string; }> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/deac/report/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createReport(params: CreateDeacReportParameters) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/report`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 204) {
            return [];
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getDashboardData(params: {
        actingBodyId?: number;
        actingBodyUnitId?: number;
        beginIn?: number;
        endIn?: number;
    }): Promise<DeacDashboardData> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/dashboard${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getVacancyApplicationsAssignments(params: {
        vacanciesApplicationIds: number[];
    }): Promise<VacancyApplicationsAssignment[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy-applications-assignments${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getAssignmentsByVacancyApplication(vacancyApplicationId: number): Promise<SimplifiedAssignment[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/assignments/${vacancyApplicationId}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getVacanciesApplications(params: CandidaciesPaginatedParameters): Promise<PaginatedResource<VacancyApplication>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancies/applications${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async cancelVacanciesApplications(params: { applicationsIds: number[]; cancellationReasonId: number; }) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancies/applications`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
    }
    async getVacanciesTemplates(params: VacanciesTemplatesPaginatedParameters): Promise<PaginatedResource<VacancyTemplate>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancies-templates${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createVacancyTemplate(params: Omit<VacancyTemplateParams, 'id'>): Promise<VacancyTemplate> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy-template`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getVacancyTemplate(id: number): Promise<VacancyTemplate> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy-template/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateVacancyTemplate(params: PartialBy<VacancyTemplateParams, 'id'>): Promise<VacancyTemplate> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy-template/${params.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteVacancyTemplate(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy-template/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 403) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async generateVacanciesFromTemplate(params: GenerateVacanciesFromTemplateParams): Promise<{ vacanciesCreatedQuantity: number; }> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/deac/vacancy-template/${params.templateId}/generate`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }
}

export const deacService = new DeacService();
